/**
 * STL sniffing, validation, and metadata extraction.
 *
 * Rendering uses three.js's STLLoader to build geometry. This module deliberately does
 * its own lightweight pass instead of reusing that, for two reasons: we want triangle
 * counts and bounds for the library listing without paying to build a BufferGeometry,
 * and this is exactly the logic a future backend would need to reimplement, so it stays
 * free of rendering dependencies.
 */

import { BoundsAccumulator } from './bounds.js';
import type { ModelEncoding, ModelStats } from './types.js';

const HEADER_BYTES = 80;
const COUNT_BYTES = 4;
/** 12 floats (a normal plus three vertices) plus a uint16 attribute count. */
const TRIANGLE_BYTES = 50;
const PREAMBLE_BYTES = HEADER_BYTES + COUNT_BYTES;
/** Offset of the first vertex within a triangle record; the normal precedes it. */
const VERTEX_OFFSET = 12;

/** Enough to see past a solid name and reach the first `facet` line. */
const SNIFF_BYTES = 1024;

/**
 * Above this, warn the user that things may get slow — a Quest 2 in particular will
 * struggle. Not a rejection: it is their file and their headset.
 */
export const TRIANGLE_WARNING_THRESHOLD = 500_000;

/** A hard ceiling, purely to avoid pathological allocations from a corrupt count field. */
export const MAX_TRIANGLES = 20_000_000;

export type StlProblem =
  | 'empty'
  | 'unrecognised'
  | 'truncated'
  | 'no-triangles'
  | 'too-many-triangles'
  | 'non-finite-vertex';

export type StlValidation =
  | { readonly ok: true; readonly stats: ModelStats }
  | { readonly ok: false; readonly problem: StlProblem; readonly message: string };

/**
 * Identify the STL flavour.
 *
 * The length formula is checked first and is decisive, because the naive test — "does it
 * start with the word `solid`?" — misfires on the many binary STLs whose 80-byte header
 * happens to begin that way. Exporters really do write those.
 */
export function detectStlFormat(buffer: ArrayBuffer): ModelEncoding | 'unknown' {
  if (buffer.byteLength >= PREAMBLE_BYTES) {
    const declared = new DataView(buffer).getUint32(HEADER_BYTES, true);
    if (PREAMBLE_BYTES + declared * TRIANGLE_BYTES === buffer.byteLength) {
      return 'binary';
    }
  }
  return looksLikeAsciiStl(buffer) ? 'ascii' : 'unknown';
}

function looksLikeAsciiStl(buffer: ArrayBuffer): boolean {
  const sniff = new TextDecoder('utf-8', { fatal: false }).decode(
    buffer.slice(0, SNIFF_BYTES),
  );

  // A NUL byte anywhere in the prefix means we are looking at binary data wearing a
  // `solid` hat, not at text.
  if (sniff.includes('\0')) return false;
  if (!/^\s*solid\b/.test(sniff)) return false;

  return /\bfacet\b/.test(sniff) || /\bendsolid\b/.test(sniff);
}

/**
 * Validate a candidate STL and extract its statistics in a single pass.
 *
 * Returns a result rather than throwing: bad uploads are an expected, routine outcome
 * that the UI reports inline, not an exceptional condition.
 */
export function validateStl(buffer: ArrayBuffer): StlValidation {
  if (buffer.byteLength === 0) {
    return fail('empty', 'The file is empty.');
  }

  const format = detectStlFormat(buffer);
  if (format === 'unknown') {
    return fail(
      'unrecognised',
      'This does not look like an STL file. Binary and ASCII STL are both supported.',
    );
  }

  return format === 'binary' ? readBinaryStl(buffer) : readAsciiStl(buffer);
}

function readBinaryStl(buffer: ArrayBuffer): StlValidation {
  const view = new DataView(buffer);
  const triangleCount = view.getUint32(HEADER_BYTES, true);

  if (triangleCount === 0) {
    return fail(
      'no-triangles',
      'The file declares no triangles, so there is nothing to show.',
    );
  }
  if (triangleCount > MAX_TRIANGLES) {
    return fail(
      'too-many-triangles',
      `The file declares ${triangleCount.toLocaleString()} triangles, which is beyond what this viewer will attempt.`,
    );
  }

  const expectedBytes = PREAMBLE_BYTES + triangleCount * TRIANGLE_BYTES;
  if (buffer.byteLength < expectedBytes) {
    return fail(
      'truncated',
      `The file claims ${triangleCount.toLocaleString()} triangles but is too short to hold them.`,
    );
  }

  const accumulator = new BoundsAccumulator();
  for (let triangle = 0; triangle < triangleCount; triangle += 1) {
    const base = PREAMBLE_BYTES + triangle * TRIANGLE_BYTES + VERTEX_OFFSET;
    for (let vertex = 0; vertex < 3; vertex += 1) {
      const offset = base + vertex * 12;
      const included = accumulator.add(
        view.getFloat32(offset, true),
        view.getFloat32(offset + 4, true),
        view.getFloat32(offset + 8, true),
      );
      if (!included) return nonFiniteVertex();
    }
  }

  return {
    ok: true,
    stats: {
      kind: 'stl',
      encoding: 'binary',
      triangleCount,
      bounds: accumulator.bounds(),
    },
  };
}

const VERTEX_PATTERN = /^\s*vertex\s+(\S+)\s+(\S+)\s+(\S+)/gm;

function readAsciiStl(buffer: ArrayBuffer): StlValidation {
  const text = new TextDecoder('utf-8', { fatal: false }).decode(buffer);

  const accumulator = new BoundsAccumulator();
  let vertexCount = 0;

  VERTEX_PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = VERTEX_PATTERN.exec(text)) !== null) {
    const included = accumulator.add(
      Number(match[1]),
      Number(match[2]),
      Number(match[3]),
    );
    if (!included) return nonFiniteVertex();
    vertexCount += 1;
  }

  if (vertexCount === 0) {
    return fail('no-triangles', 'No vertices were found, so there is nothing to show.');
  }
  if (vertexCount % 3 !== 0) {
    return fail(
      'truncated',
      `The file has ${vertexCount.toString()} vertices, which is not a whole number of triangles.`,
    );
  }

  return {
    ok: true,
    stats: {
      kind: 'stl',
      encoding: 'ascii',
      triangleCount: vertexCount / 3,
      bounds: accumulator.bounds(),
    },
  };
}

function fail(problem: StlProblem, message: string): StlValidation {
  return { ok: false, problem, message };
}

function nonFiniteVertex(): StlValidation {
  return fail(
    'non-finite-vertex',
    'The file contains a vertex that is not a finite number, so it cannot be rendered.',
  );
}
